import {
  type FC, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from '~/_shared/utils/hooks';
import { AppErrorType } from '~/appError/appErrorType.enum';
import {
  type ModalProps, ModalType,
} from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useBoundaryEditAnother } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundaryEditAnother';
import { useBoundarySelectEditCustomBoundary } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/useBoundarySelectEditCustomBoundary';
import { type BoundaryCombineGeometryRequestItem } from '~/store/boundaries/boundaries.repository';
import type {
  BoundaryGroupId, BoundaryId,
} from '~/store/boundaries/boundaryIdentifier.type';
import { useBoundaryIsCustom } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useMapBoundariesSelector } from '~/store/boundaryItems/boundaryItems.selectors';
import { useBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import {
  boundarySelectEditCloseModal, boundarySelectEditCustomBoundarySubmitOpenModal,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import {
  type BoundarySelectState, isBoundarySelectEditCustomBoundarySubmit,
} from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';
import { createAppError } from '~/store/modal/modal.actionCreators';
import { type BoundaryEditManualMethod } from '../boundaryEditModal/boundaryEditModal.helpers';
import { useBoundaryEditActions } from './boundaryEditSubmitActions';
import {
  BoundaryEditSubmitModalComponent, type BoundaryEditSubmitOption,
} from './boundaryEditSubmitModal.component';
import { BoundaryEditSubmitModalAction } from './boundaryEditSubmitModal.helpers';

export type BoundaryEditSubmitModalContainerProps = ModalProps<{
  editedBoundaryTerritoryGroupId: number;
  geometry: BoundaryCombineGeometryRequestItem[];
  baseGeometry?: BoundaryCombineGeometryRequestItem[];
  onSubmit: (editAnother: boolean) => void;
  method: BoundaryEditManualMethod;
  selectedBoundaryId?: BoundaryId;
  submitAction?: BoundaryEditSubmitModalAction;
  isBoundarySelectDisabled: boolean;
  selectBoundaryGroupId?: BoundaryGroupId; // used when editing boundary via selection
  hideEditAnother?: boolean;
}>;

export const BoundaryEditSubmitModalContainer: FC<BoundaryEditSubmitModalContainerProps> = (props) => {
  const { editedBoundaryTerritoryGroupId } = props;
  const { openModal: openBoundaryEditSubmitModal } = useModal(ModalType.BoundaryEditSubmit);
  const { onBoundaryEditSubmit } = useBoundaryEditAnother();
  const { isLoading, editBoundary } = useBoundaryEditActions({ editedBoundaryTerritoryGroupId });
  const boundaryTerritoryGroups = useBoundaryTerritoryGroupsSelector();
  const editedBoundaryTerritoryGroup = boundaryTerritoryGroups
    .find(group => group.boundaryTerritoryGroupId === editedBoundaryTerritoryGroupId);
  const boundaryItems = useMapBoundariesSelector();
  const groupBoundaries = editedBoundaryTerritoryGroup ? boundaryItems.get(editedBoundaryTerritoryGroup.boundaryGroupId) : null;
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const { onClose, onSubmit } = props;
  const { onBoundarySelectEditCustomBoundarySubmit } = useBoundarySelectEditCustomBoundary();

  const isBoundaryGroupCustom = useBoundaryIsCustom(editedBoundaryTerritoryGroup?.boundaryGroupId);

  const onSelectBoundaryFromMapClick = useCallback((action: BoundaryEditSubmitModalAction) => {
    if (!editedBoundaryTerritoryGroup) {
      return;
    }

    onClose();

    const onBoundarySelectSubmit = (selectState: BoundarySelectState) => {
      if (!isBoundarySelectEditCustomBoundarySubmit(selectState)) {
        return;
      }

      const boundaryId = selectState.selectedBoundaryInfos[0]?.boundaryId ?? null;

      if (boundaryId === null) {
        return;
      }

      openBoundaryEditSubmitModal({
        editedBoundaryTerritoryGroupId: selectState.selectionModeData.boundaryTerritoryGroupId,
        selectedBoundaryId: boundaryId,
        method: selectState.selectionModeData.configuration.method,
        geometry: selectState.selectionModeData.configuration.geometry,
        baseGeometry: selectState.selectionModeData.configuration.baseGeometry,
        submitAction: selectState.selectionModeData.configuration.submitAction,
        isBoundarySelectDisabled: false,
        selectBoundaryGroupId: selectState.selectionModeData.configuration.previousBoundaryGroupId,
        onSubmit: (editAnother) => {
          onBoundaryEditSubmit({
            editAnother,
            editedBoundaryTerritoryGroupId: selectState.selectionModeData.boundaryTerritoryGroupId,
            selectMethodBoundaryGroupId: selectState.selectionModeData.configuration.previousBoundaryGroupId,
            method: selectState.selectionModeData.configuration.method,
            onEditAnotherSubmit: onBoundarySelectEditCustomBoundarySubmit,
            onEditAnotherClose: () => {
              dispatch(boundarySelectEditCloseModal());
            },
          });
        },
      });
      dispatch(boundarySelectEditCloseModal());
    };

    dispatch(boundarySelectEditCustomBoundarySubmitOpenModal({
      selectBoundaryGroupId: editedBoundaryTerritoryGroup.boundaryGroupId,
      boundaryTerritoryGroupId: editedBoundaryTerritoryGroup.boundaryTerritoryGroupId,
      configuration: {
        geometry: props.geometry,
        baseGeometry: props.baseGeometry,
        submitAction: action,
        previousBoundaryGroupId: props.selectBoundaryGroupId,
        method: props.method,
      },
      onClose: () => {
        dispatch(boundarySelectEditCloseModal());
      },
      onSubmit: onBoundarySelectSubmit,
    }));
  }, [editedBoundaryTerritoryGroup, onClose, dispatch, props.geometry, props.baseGeometry, props.selectBoundaryGroupId,
    props.method, openBoundaryEditSubmitModal, onBoundaryEditSubmit, onBoundarySelectEditCustomBoundarySubmit]);

  const handleSubmit = useCallback((submitData: BoundaryEditSubmitOption, editAnother: boolean) => {
    if (!editedBoundaryTerritoryGroup || !groupBoundaries) {
      return;
    }

    const onSuccess = () => {
      onSubmit(editAnother);
      onClose();
    };

    const onError = () => {
      let errorMessage: string;

      switch (submitData.action) {
        case BoundaryEditSubmitModalAction.AddToExistingTerritory:
          errorMessage = t('Error adding area to an existing territory');
          break;
        case BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory:
          errorMessage = t('Error removing area from territory');
          break;
        case BoundaryEditSubmitModalAction.ReassignTerritory:
          errorMessage = t('Error reassigning area');
          break;
        default:
          errorMessage = '';
      }

      dispatch(createAppError({
        type: AppErrorType.General,
        title: errorMessage,
      }));
    };

    const baseGeometry = props.baseGeometry ?? getDefaultBaseGeometry(submitData, editedBoundaryTerritoryGroup.boundaryGroupId);

    editBoundary(submitData, baseGeometry, props.geometry, onSuccess, onError);
  }, [dispatch, editBoundary, editedBoundaryTerritoryGroup, groupBoundaries, onClose, onSubmit, props.baseGeometry, props.geometry, t]);

  if (
    !editedBoundaryTerritoryGroup ||
    !groupBoundaries ||
    !isBoundaryGroupCustom ||
    props.geometry.length === 0
  ) {
    return null;
  }

  return (
    <BoundaryEditSubmitModalComponent
      isLoading={isLoading}
      isOpen={props.isOpen}
      onClose={props.onClose}
      onSubmit={handleSubmit}
      boundaries={Array.from(groupBoundaries.values())}
      method={props.method}
      selectedBoundaryId={props.selectedBoundaryId}
      editedBoundaryTerritoryGroup={editedBoundaryTerritoryGroup}
      geometry={props.geometry}
      submitAction={props.submitAction}
      isBoundarySelectDisabled={props.isBoundarySelectDisabled}
      hideBoundarySelectWhenDisabled
      onSelectBoundaryFromMapClick={onSelectBoundaryFromMapClick}
      hideEditAnother={props.hideEditAnother ?? false}
    />
  );
};

const getDefaultBaseGeometry = (submitData: BoundaryEditSubmitOption, boundaryGroupId: BoundaryGroupId): BoundaryCombineGeometryRequestItem[] => {
  const boundaryId = submitData.action === BoundaryEditSubmitModalAction.ReassignTerritory
    ? submitData.targetBoundaryId
    : submitData.selectedBoundaryId;

  return [{
    type: 'boundary',
    boundary_id: boundaryId,
    boundary_group_id: boundaryGroupId,
  }];
};
